import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  type AvailabilityStatus,
  type Contractor,
  type ContractorPayProfile,
  type SpecArchetype,
  type ContractorTier,
  SPEC_ARCHETYPES,
  defaultPayProfile,
} from "@/data/contractorPayProfile";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contractorId: string;
  contractorName: string;
  contractor?: Contractor;
  existing?: ContractorPayProfile;
  onSave: (profile: ContractorPayProfile, contractorPatch?: Partial<Contractor>) => void;
}

function parseList(value: string): string[] {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

/**
 * Installer Eligibility Profile form.
 *
 * Governs assignment eligibility only. Per-job payout amounts live on the
 * MarketplaceOffer (payoutAmountSnapshot) and are set when an opportunity is
 * offered — never here. Financial fields on the underlying type are retained
 * for backward compatibility but are not editable from this surface.
 */
export function PayProfileForm({ open, onOpenChange, contractorId, contractorName, contractor, existing, onSave }: Props) {
  const [form, setForm] = useState<ContractorPayProfile>(() =>
    existing ?? { id: `PP-${contractorId}`, ...defaultPayProfile(contractorId) },
  );
  const [serviceArea, setServiceArea] = useState(contractor?.serviceArea ?? "");
  const [serviceRegionsText, setServiceRegionsText] = useState((contractor?.serviceRegions ?? []).join(", "));
  const [serviceZipCodesText, setServiceZipCodesText] = useState((contractor?.serviceZipCodes ?? []).join(", "));
  const [availabilityStatus, setAvailabilityStatus] = useState<AvailabilityStatus>(contractor?.availabilityStatus ?? "Available");
  const [maxWeeklyJobs, setMaxWeeklyJobs] = useState(contractor?.maxWeeklyJobs ?? 0);

  useEffect(() => {
    if (open) {
      setForm(existing ?? { id: `PP-${contractorId}`, ...defaultPayProfile(contractorId) });
      setServiceArea(contractor?.serviceArea ?? "");
      setServiceRegionsText((contractor?.serviceRegions ?? []).join(", "));
      setServiceZipCodesText((contractor?.serviceZipCodes ?? []).join(", "));
      setAvailabilityStatus(contractor?.availabilityStatus ?? "Available");
      setMaxWeeklyJobs(contractor?.maxWeeklyJobs ?? 0);
    }
  }, [open, existing, contractorId, contractor]);

  const toggleSpec = (spec: SpecArchetype, checked: boolean) => {
    setForm((f) => ({
      ...f,
      allowedSpecTypes: checked
        ? [...f.allowedSpecTypes, spec]
        : f.allowedSpecTypes.filter((s) => s !== spec),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.allowedSpecTypes.length === 0) return;
    onSave(
      { ...form, updatedAt: new Date().toISOString() },
      {
        serviceArea: serviceArea.trim(),
        serviceRegions: parseList(serviceRegionsText),
        serviceZipCodes: parseList(serviceZipCodesText),
        availabilityStatus,
        maxWeeklyJobs: Math.max(0, Number(maxWeeklyJobs) || 0),
      },
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {existing ? "Edit" : "Create"} Installer Eligibility Profile
            <span className="block text-xs font-normal text-muted-foreground mt-0.5">{contractorName}</span>
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Governs which work this installer is eligible for. Per-job payout amounts are set on the offer, not here.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Allowed Spec Types */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Allowed Spec Types <span className="text-destructive">*</span>
            </Label>
            <p className="text-[11px] text-muted-foreground mb-2">Which job specs this installer can execute.</p>
            <div className="grid grid-cols-2 gap-2 p-3 rounded-md border bg-muted/20">
              {SPEC_ARCHETYPES.map((spec) => (
                <label key={spec.id} className="flex items-start gap-2 text-xs cursor-pointer">
                  <Checkbox
                    checked={form.allowedSpecTypes.includes(spec.id)}
                    onCheckedChange={(c) => toggleSpec(spec.id, c === true)}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="font-mono font-semibold">{spec.id}</span>
                    <span className="block text-[10px] text-muted-foreground">{spec.label}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Service Scope */}
          <div className="space-y-3 rounded-md border bg-muted/10 p-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Service Scope
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Controls the service regions, ZIP codes, availability, and capacity shown on the installer details card.
              </p>
            </div>

            <div>
              <Label htmlFor="serviceArea" className="text-xs">Service Area Summary</Label>
              <Input
                id="serviceArea"
                value={serviceArea}
                onChange={(e) => setServiceArea(e.target.value)}
                className="mt-1"
                placeholder="Greater Tampa Bay"
              />
            </div>

            <div>
              <Label htmlFor="serviceRegions" className="text-xs">Service Regions</Label>
              <Textarea
                id="serviceRegions"
                rows={2}
                value={serviceRegionsText}
                onChange={(e) => setServiceRegionsText(e.target.value)}
                className="mt-1"
                placeholder="Tampa, Tampa Bay, Central Florida, Orlando"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">Separate regions with commas or new lines.</p>
            </div>

            <div>
              <Label htmlFor="serviceZipCodes" className="text-xs">Service ZIP Codes</Label>
              <Textarea
                id="serviceZipCodes"
                rows={2}
                value={serviceZipCodesText}
                onChange={(e) => setServiceZipCodesText(e.target.value)}
                className="mt-1 font-mono"
                placeholder="33601, 33602, 33603"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">Separate ZIP codes with commas or new lines.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Availability</Label>
                <Select
                  value={availabilityStatus}
                  onValueChange={(v) => setAvailabilityStatus(v as AvailabilityStatus)}
                >
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Available">Available</SelectItem>
                    <SelectItem value="Limited">Limited</SelectItem>
                    <SelectItem value="Unavailable">Unavailable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="maxWeeklyJobs" className="text-xs">Capacity (jobs/week)</Label>
                <Input
                  id="maxWeeklyJobs"
                  type="number"
                  min={0}
                  step={1}
                  value={maxWeeklyJobs}
                  onChange={(e) => setMaxWeeklyJobs(Number(e.target.value) || 0)}
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Tier */}
          <div>
            <Label className="text-xs">Reliability Tier</Label>
            <Select
              value={form.tierLevel ?? "standard"}
              onValueChange={(v) => setForm((f) => ({ ...f, tierLevel: v as ContractorTier }))}
            >
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="preferred">Preferred</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Influences dispatch priority. Reliability score is system-derived.
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes" className="text-xs">Operating Notes (optional)</Label>
            <Textarea
              id="notes" rows={2}
              value={form.notes ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="mt-1"
              placeholder="Coverage area, capacity, dispatcher notes…"
            />
          </div>

          {/* Active */}
          <div className="flex items-center justify-between p-3 rounded-md border bg-muted/20">
            <div>
              <Label className="text-xs font-semibold">Profile Active</Label>
              <p className="text-[10px] text-muted-foreground">Inactive profiles block all new assignments.</p>
            </div>
            <Switch
              checked={form.isActive}
              onCheckedChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
            />
          </div>

          <div className="rounded-md border border-dashed bg-muted/10 px-3 py-2">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <span className="font-semibold text-foreground">Payout amounts are not set here.</span>{" "}
              Each opportunity carries its own payout snapshot, set at offer time and visible in the installer portal.
            </p>
          </div>

          {/* ── Legacy Economics (Deprecated) ──────────────────────────────
              These fields predate the JOB-based / OFFER-based compensation
              model. They remain on the type for back-compat, but are NOT
              consulted by dispatch, offer creation, or payout. Per-offer
              amounts live on MarketplaceOffer.payoutAmountSnapshot. */}
          <details className="rounded-md border border-dashed bg-muted/10">
            <summary className="cursor-pointer select-none px-3 py-2 text-[11px] font-semibold text-muted-foreground hover:text-foreground">
              Legacy Economics (Deprecated)
            </summary>
            <div className="px-3 pb-3 pt-1 space-y-3">
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                Retained for historical records only. Compensation is now JOB-based
                and OFFER-based — these values are not used by dispatch or payout.
              </p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label htmlFor="legacy-totalPayout" className="text-[10px]">Static Payout ($)</Label>
                  <Input
                    id="legacy-totalPayout" type="number" min={0} step={50}
                    value={form.totalPayoutAmount}
                    onChange={(e) => setForm((f) => ({ ...f, totalPayoutAmount: Number(e.target.value) || 0 }))}
                    className="mt-1 h-8 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="legacy-sla" className="text-[10px]">SLA (hrs)</Label>
                  <Input
                    id="legacy-sla" type="number" min={0} step={1}
                    value={form.paymentSlaHours}
                    onChange={(e) => setForm((f) => ({ ...f, paymentSlaHours: Number(e.target.value) || 0 }))}
                    className="mt-1 h-8 text-xs"
                  />
                </div>
                <div>
                  <Label htmlFor="legacy-fieldAdd" className="text-[10px]">Field-Add Cap ($)</Label>
                  <Input
                    id="legacy-fieldAdd" type="number" min={0} step={25}
                    value={form.fieldAddCapAmount}
                    onChange={(e) => setForm((f) => ({ ...f, fieldAddCapAmount: Number(e.target.value) || 0 }))}
                    className="mt-1 h-8 text-xs"
                  />
                </div>
              </div>
            </div>
          </details>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={form.allowedSpecTypes.length === 0}>
              {existing ? "Save Changes" : "Create Eligibility Profile"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
